export default function Privacy() {
  return (
    <div className="pb-10 md:p-10 flex justify-center mt-[80px]">
      <div className="max-w-[1200px] w-full flex flex-col break-words justify-center">
        <div className="mb-10">
          <div className="font-bold text-2xl text-center">CTrees Privacy Policy</div>
        </div>
        <div className="flex flex-col justify-center mx-4">
          <p>
            <span>Last updated: October 10, 2024</span>
          </p>

          <p>
            <span>
              At CTrees (&ldquo;CTrees&rdquo;, &ldquo;we&rdquo;, &ldquo;our&rdquo;, or &ldquo;us&rdquo;), the privacy of our subscribers is of the utmost importance. Thank you for taking the time to read our privacy policy (the &ldquo;Privacy Policy&rdquo;). CTrees is a not-for-profit organization that tracks forest carbon around the world. Subscribers to your services can access and download data and other information associated with our tracking activities (collectively, the &ldquo;Services&rdquo;). This Privacy Policy sets out how we collect, use, process, store, and disclose your personal information on www.ctrees.org (the &ldquo;Website&rdquo;) and the Services.
            </span>
          </p>

          <p>
            <span>
              By accessing and using our Services you freely and expressly consent to the collection, use, processing, storage, and disclosure of your personal information as set out in this Privacy Policy and our Terms of Service.
            </span>
          </p>

          <p>&nbsp;</p>

          <div className="mb-6">
            <h3 className="font-bold text-lg mb-4">Table of Contents</h3>
            <ul className="list-decimal list-inside space-y-2">
              <li><a href="#section1" className="text-blue-600 hover:underline">When We Collect Your Personal Information</a></li>
              <li><a href="#section2" className="text-blue-600 hover:underline">Personal Information We Collect</a></li>
              <li><a href="#section3" className="text-blue-600 hover:underline">How We Use Your Information</a></li>
              <li><a href="#section4" className="text-blue-600 hover:underline">How We Disclose Your Personal Information</a></li>
              <li><a href="#section5" className="text-blue-600 hover:underline">Your Privacy Choices</a></li>
              <li><a href="#section6" className="text-blue-600 hover:underline">Use of Cookies and Tracking Technologies</a></li>
              <li><a href="#section7" className="text-blue-600 hover:underline">U.S. State-Specific Disclosure</a></li>
              <li><a href="#section8" className="text-blue-600 hover:underline">UK/EEA Residents&rsquo; Notice</a></li>
              <li><a href="#section9" className="text-blue-600 hover:underline">Security</a></li>
              <li><a href="#section10" className="text-blue-600 hover:underline">Retention</a></li>
              <li><a href="#section11" className="text-blue-600 hover:underline">Third-Party Links On Our Website</a></li>
              <li><a href="#section12" className="text-blue-600 hover:underline">Changes To This Privacy Policy</a></li>
              <li><a href="#section13" className="text-blue-600 hover:underline">Contact Information</a></li>
            </ul>
          </div>

          <p>&nbsp;</p>

          <div id="section1" className="scroll-mt-20">
            <h2 className="font-bold text-xl mb-4 mt-2">
              <span>1. When We Collect Your Personal Information</span>
            </h2>
            <p>
              <span>
                We may collect personal information that you provide when you:
              </span>
            </p>
            <ul className="list-disc list-inside ml-4 mt-2 space-y-1">
              <li>Visit our Website.</li>
              <li>Register for our Services.</li>
              <li>Use our Services.</li>
              <li>Sign up for our email newsletter.</li>
              <li>Contact or otherwise correspond with us.</li>
            </ul>
          </div>

          <p>&nbsp;</p>

          <div id="section2" className="scroll-mt-20">
            <h2 className="font-bold text-xl mb-4 mt-2">
              <span>2. Personal Information We Collect</span>
            </h2>
            <p>
              <span>
                <strong>Information you provide:</strong>
              </span>
            </p>
            <p>
              <span>
                <strong>Account Creation.</strong> When you register to our Services (&ldquo;Subscriber(s)&rdquo;), we collect contact details such as your name and email address.
              </span>
            </p>
            <p>
              <span>
                <strong>Correspondence.</strong> We may collect information from your inquiries into our services, or how you otherwise indicate you may be interested in our services, which may include signing up for email communications or providing us with your name, work email, phone number, company name, position title, website, social media links, and time zone.
              </span>
            </p>
            <p>
              <span>
                <strong>Institutional Account Creation.</strong> If you subscribe to CTrees in the context of your employment, we may collect, in addition to the &lsquo;Account Creation Information&rsquo; detailed above, the company name, work email, your position title, and the payment information of your employer.
              </span>
            </p>
            <p>
              <span>
                <strong>Information we automatically collect:</strong>
              </span>
            </p>
            <p>
              <span>
                <strong>Search Query Data.</strong> We collect your search query data you enter into our Services in order to improve the Services, to improve the results for that Subscriber and improve performance with the overall Services.
              </span>
            </p>
            <p>
              <span>
                <strong>Metadata and Analytics.</strong> We may collect Metadata and analytics of your use of our Services, including IP address, device information, date/time of visits, new or returning visits, products viewed, page response times, URL clickstreams, how long you stay on our pages, what you do on those pages.
              </span>
            </p>
            <p>
              <span>
                We do not intentionally collect any personal information from children under the age of 18. If you believe we have obtained personal information associated with children under the age of 18, please contact us at info@ctrees.org and we will delete it.
              </span>
            </p>
          </div>

          <p>&nbsp;</p>

          <div id="section3" className="scroll-mt-20">
            <h2 className="font-bold text-xl mb-4 mt-2">
              <span>3. How We Use Your Information</span>
            </h2>
            <p>
              <span>
                We use the personal information we collect for the following purposes:
              </span>
            </p>
            <ul className="list-disc list-inside ml-4 mt-2 space-y-1">
              <li>To provide and operate our Services, including facilitating account creation and managing user accounts as well as providing our software or services.</li>
              <li>To improve our Services, for example, we use your information to better our Services.</li>
              <li>To identify usage trends and otherwise measure all activities on the Services.</li>
              <li>For customer support, including via email.</li>
              <li>To communicate with you, including via our newsletter or to request feedback.</li>
              <li>To protect our Services and keep our Services safe and securing, including fraud monitoring and prevention.</li>
              <li>For other business and legal purposes, such as to collect amounts owing to us, and to maintain business records.</li>
              <li>With your consent.</li>
            </ul>
          </div>

          <p>&nbsp;</p>

          <div id="section4" className="scroll-mt-20">
            <h2 className="font-bold text-xl mb-4 mt-2">
              <span>4. How We Disclose Your Personal Information</span>
            </h2>
            <p>
              <span>
                We may disclose your information with your consent. We may also disclose your information with our corporate affiliates, as well as vendors or service providers who:
              </span>
            </p>
            <ul className="list-disc list-inside ml-4 mt-2 space-y-1">
              <li>Help us communicate with you, such as through our email service provider.</li>
              <li>Manage and optimize our Website and Services, such as through Google Analytics.</li>
              <li>Provide cloud hosting, such as through Amazon Web Services.</li>
              <li>Provide analytics/measurement.</li>
              <li>Help us provide customer support.</li>
            </ul>
            <p>
              <span>
                In addition to the above, we may disclose your information for the following purposes:
              </span>
            </p>
            <p>
              <span>
                <strong>Legal and Compliance Purposes.</strong> We may share information with external parties when complying with legal process (eg; subpoena), to protect our intellectual property and other legal rights, to prevent fraud or imminent harm, and secure the use of our Services. We reserve the right to disclose information as required by law and when we believe that disclosure is necessary to protect our rights and/or comply with a law enforcement order, such as a search warrant, judicial proceeding, or court order. We may also retain and use information as necessary to comply with our legal obligations, resolve disputes, and enforce our Terms of Service.
              </span>
            </p>
            <p>
              <span>
                <strong>Business Transactions.</strong> In the event that CTrees goes through a business transaction, such as a merger, acquisition by another company, or sale of all or a portion of its assets, bankruptcy, or other corporate change, including, without limitation, during the course of any due diligence process, subscriber information, including Personal Information, will likely be among the assets shared and transferred. Subscribers will be notified via email and/or a prominent notice on the Services of any completed change in ownership or materially different uses of their personal information. This Privacy Policy will become binding upon the new owner of the information until amended.
              </span>
            </p>
            <p>
              <span>
                <strong>Anonymous Data.</strong> We may use and disclose anonymous or aggregated information about our children&rsquo;s subscribers to any external parties, including current and potential business partners/affiliates, investors, or the public, or use it for research or analytics purposes.
              </span>
            </p>
          </div>

          <p>&nbsp;</p>

          <div id="section5" className="scroll-mt-20">
            <h2 className="font-bold text-xl mb-4 mt-2">
              <span>5. Your Privacy Choices</span>
            </h2>
            <p>
              <span>
                CTrees provides you with the ability to exercise the following choices with our use of your personal information:
              </span>
            </p>
            <ul className="list-disc list-inside ml-4 mt-2 space-y-1">
              <li>Access the personal information we maintain about you.</li>
              <li>Delete the personal information we maintain about you.</li>
              <li>Correct inaccurate personal information we maintain about you</li>
              <li>Opt out of certain uses of your personal information, notably, you can unsubscribe to our email list by clicking the unsubscribe link at the bottom of marketing emails.</li>
            </ul>
            <p>
              <span>
                You can exercise these rights by contacting us at info@ctrees.org
              </span>
            </p>
            <p>
              <span>
                If you would at any time like to review or change the information in your account or terminate your account, you can contact us using the contact information provided. Upon your request to terminate your account, we will deactivate or delete your account and information from our active databases. However, we may retain some information in our files to prevent fraud, troubleshoot problems, assist with any investigations, enforce our legal terms and/or comply with applicable legal requirements.
              </span>
            </p>
          </div>

          <p>&nbsp;</p>

          <div id="section6" className="scroll-mt-20">
            <h2 className="font-bold text-xl mb-4 mt-2">
              <span>6. Use of Cookies and Tracking Technologies</span>
            </h2>
            <p>
              <span>
                We may use cookies and similar tracking technologies like web beacons and pixels (&ldquo;cookies&rdquo;) to access or store information for functional and analytics purposes.
              </span>
            </p>
            <p>
              <span>
                Most browsers let you remove or reject cookies. To do this, follow the instructions in your browser settings. Many browsers accept cookies by default until you change your settings. Please note that because this opt-out is specific to the device or browser on which it is exercised, you will need to opt out on every browser and device that you use.
              </span>
            </p>
            <p>
              <span>
                We currently do not use third party advertising cookies on our website.
              </span>
            </p>
          </div>

          <p>&nbsp;</p>

          <div id="section7" className="scroll-mt-20">
            <h2 className="font-bold text-xl mb-4 mt-2">
              <span>7. U.S. State-Specific Disclosure</span>
            </h2>
            <p>
              <span>
                Some U.S. states have enacted comprehensive privacy laws related to the &lsquo;sale&rsquo; or &lsquo;sharing&rsquo; of personal information for advertising purposes, as well as use of &lsquo;sensitive&rsquo; personal information. CTrees does not &lsquo;sell&rsquo; or &lsquo;share&rsquo; personal information for targeted or cross-contextual behavioral advertising purposes nor collect sensitive personal information.
              </span>
            </p>
          </div>

          <p>&nbsp;</p>

          <div id="section8" className="scroll-mt-20">
            <h2 className="font-bold text-xl mb-4 mt-2">
              <span>8. UK/EEA Residents Notice</span>
            </h2>
            <p>
              <span>
                Residents of the United Kingdom (&ldquo;UK&rdquo;) and European Economic Area (&ldquo;EEA&rdquo;) are provided certain privacy rights under the UK and EU General Data Protection Regulations (&ldquo;GDPR&rdquo;).
              </span>
            </p>
            <p>
              <span>
                <strong>Legal Basis:</strong>
              </span>
            </p>
            <p>
              <span>
                Under the GDPR, we process &lsquo;Personal Data&rsquo; (as defined in the GDPR) under the following legal basis.
              </span>
            </p>
            <div className="overflow-x-auto mt-4">
              <table className="min-w-full border border-gray-300">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="border border-gray-300 px-4 py-2 text-left">Processing Activity</th>
                    <th className="border border-gray-300 px-4 py-2 text-left">Legal Basis under GDPR</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="border border-gray-300 px-4 py-2">Providing our Services</td>
                    <td className="border border-gray-300 px-4 py-2">Contract Fulfillment</td>
                  </tr>
                  <tr>
                    <td className="border border-gray-300 px-4 py-2">Improving our Website and Services</td>
                    <td className="border border-gray-300 px-4 py-2">Legitimate Interest</td>
                  </tr>
                  <tr>
                    <td className="border border-gray-300 px-4 py-2">Product, Marketing or Service-Related Communications</td>
                    <td className="border border-gray-300 px-4 py-2">Legitimate Interest</td>
                  </tr>
                  <tr>
                    <td className="border border-gray-300 px-4 py-2">Customer support</td>
                    <td className="border border-gray-300 px-4 py-2">Contract fulfillment</td>
                  </tr>
                  <tr>
                    <td className="border border-gray-300 px-4 py-2">Use of Pixel Tags or Cookies</td>
                    <td className="border border-gray-300 px-4 py-2">Consent</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p>
              <span>
                <strong>Controller Designation:</strong>
              </span>
            </p>
            <p>
              <span>
                Under the GDPR, we are designated as a &lsquo;Controller.&rsquo;
              </span>
            </p>
            <p>
              <span>
                <strong>Cross-Border Data Transfers:</strong>
              </span>
            </p>
            <p>
              <span>
                All data is stored in the United States. As such, if you are a resident of the EEA, UK, or Switzerland, we may transfer to, and store the data we collect about you, to countries other than the country in which the data was originally collected, including the United States. Those countries may not have equivalent data protection laws as the country in which you provided the data. When we transfer your data to other countries, we will protect the data as described in this Privacy Policy and comply with applicable legal requirements providing adequate protection for the transfer of data to countries outside the EEA, UK, and Switzerland. We rely on Standard Contractual Clauses (&ldquo;SCCs&rdquo;) for the transfer of personal data to countries that have not received an applicable adequacy decision, as well as the UK&rsquo;s &lsquo;International Data Transfer Addendum&rsquo; to the SCCs.
              </span>
            </p>
            <p>
              <span>
                For more information on cross-border transfers of your Personal Data or the appropriate safeguards in place, please contact us at info@ctrees.org
              </span>
            </p>
            <p>
              <span>
                <strong>Additional Rights for UK or European Economic Area Residents:</strong>
              </span>
            </p>
            <p>
              <span>
                In addition to the rights granted above, if you are a UK or EEA resident, the GDPR grants you the right to lodge a complaint against us with your local data protection authority. You can find your data protection authority at{" "}
                <a href="https://edpb.europa.eu/about-edpb/about-edpb/members_en" className="text-blue-600 hover:underline">
                  https://edpb.europa.eu/about-edpb/about-edpb/members_en
                </a>
                .
              </span>
            </p>
          </div>

          <p>&nbsp;</p>

          <div id="section9" className="scroll-mt-20">
            <h2 className="font-bold text-xl mb-4 mt-2">
              <span>9. Security</span>
            </h2>
            <p>
              <span>
                We protect information we collect about you by maintaining physical, electronic and procedural safeguards designed to prevent unauthorized access to user data retained in our servers, however, due to the inherent open nature of the internet, we cannot ensure or warrant the security of any information provided online. We take reasonable precautions to protect your information both online and offline. The servers on which we store data are kept in a secure environment.
              </span>
            </p>
          </div>

          <p>&nbsp;</p>

          <div id="section10" className="scroll-mt-20">
            <h2 className="font-bold text-xl mb-4 mt-2">
              <span>10. Retention</span>
            </h2>
            <p>
              <span>
                We will retain your subscriber information for as long as your account or inquiry is active or as needed to provide you with the website or any requested services, and for a reasonable time thereafter in accordance with our standard procedures or as necessary to comply with our legal obligations, to resolve disputes, and to enforce our agreements. Even if we delete some or all of your personal information, we may continue to retain and use aggregate or anonymous data previously collected and/or anonymize or aggregate your Personal Information. Please note that we will not be liable for disclosures of your data due to errors or unauthorized acts of third parties.
              </span>
            </p>
          </div>

          <p>&nbsp;</p>

          <div id="section11" className="scroll-mt-20">
            <h2 className="font-bold text-xl mb-4 mt-2">
              <span>11. Third-Party Links On Our Website</span>
            </h2>
            <p>
              <span>
                Our Services may contain links to other websites or services. We do not exercise control over the information you provide, or is collected by these third party websites. We encourage you to read the privacy policies or statements of the other websites you visit.
              </span>
            </p>
          </div>

          <p>&nbsp;</p>

          <div id="section12" className="scroll-mt-20">
            <h2 className="font-bold text-xl mb-4 mt-2">
              <span>12. Changes To This Privacy Policy</span>
            </h2>
            <p>
              <span>
                We may update this Privacy Policy from time to time. If we make material changes, we will communicate directly with you via email and/or post the updated Privacy Policy on this page with a &ldquo;Last Updated&rdquo; effective date of the revisions. We encourage you to look for updates and changes to this Privacy Policy by checking this page when you access our Services.
              </span>
            </p>
          </div>

          <p>&nbsp;</p>

          <div id="section13" className="scroll-mt-20">
            <h2 className="font-bold text-xl mb-4 mt-2">
              <span>13. Contact Information</span>
            </h2>
            <p>
              <span>
                If you have any privacy-related questions, you can contact us at{" "}
                <a href="mailto:info@ctrees.org" className="text-blue-600 hover:underline">
                  info@ctrees.org
                </a>{" "}
                or
              </span>
            </p>
            <div className="mt-4">
              <p>
                <span>CTrees</span>
              </p>
              <p>
                <span>12 S Raymond Ave, Suite B</span>
              </p>
              <p>
                <span>Pasadena, CA 91105</span>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
